import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, LogOut } from 'lucide-react';
import { useAuthStore } from '@core/authStore';
import { projectsService, type Project } from '@core/projects';
import { ordersService, type OrderSummary } from '@core/orders';
import { useAtlasStore } from '@/stores/atlasStore';
import { getBoundsFromPolygon } from '@/services/api';

type Props = {
  orderId: string | null;
  projectName: string | null;
};

function shortId(id: string): string {
  return id.slice(0, 8);
}

function orderUrl(id: string): string {
  // Full navigation (not client-side) so the viewer re-bootstraps cleanly for
  // the chosen order instead of layering on top of a previous order's state.
  return window.location.origin + import.meta.env.BASE_URL + '?order=' + id;
}

/**
 * Project + order switcher for the TopBar (authenticated users only).
 *
 * Replaces the old placeholder pill. Lists the user's projects; each expands
 * (accordion) to its orders; selecting an order loads it into the viewer. With
 * no projects, the only action is "Criar novo projeto".
 */
export function ProjectSwitcher({ orderId, projectName }: Props) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const setOrderPolygon = useAtlasStore((s) => s.setOrderPolygon);
  const setOrderBounds = useAtlasStore((s) => s.setOrderBounds);
  const setCreateMode = useAtlasStore((s) => s.setCreateMode);

  // On the empty atlas (no order loaded), expanding a project focuses the map
  // on its polygon. Skipped when an order is loaded so we don't disturb it.
  const focusProject = (p: Project) => {
    if (orderId || !p.polygon_geojson) return;
    setOrderPolygon(p.polygon_geojson);
    const bounds = getBoundsFromPolygon(p.polygon_geojson);
    if (bounds) setOrderBounds(bounds);
  };

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [orders, setOrders] = useState<OrderSummary[] | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Lazy-load on first open so public order viewers never fetch.
  useEffect(() => {
    if (!open || projects !== null) return;
    let cancelled = false;
    Promise.all([projectsService.getProjects(), ordersService.getOrders()]).then(
      ([p, o]) => {
        if (cancelled) return;
        if (p.error || o.error) setLoadError(true);
        setProjects(p.projects);
        setOrders(o.orders);
      },
    );
    return () => { cancelled = true; };
  }, [open, projects]);

  const ordersByProject = useMemo(() => {
    const map = new Map<string, OrderSummary[]>();
    for (const o of orders ?? []) {
      const key = o.project_id ?? '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    return map;
  }, [orders]);

  if (!isAuthenticated) return null;

  const label = projectName || (orderId ? `Pedido ${shortId(orderId)}` : 'Os meus projetos');
  const loaded = projects !== null;
  const orphanOrders = ordersByProject.get('__none__') ?? [];

  return (
    <div className="relative hidden sm:block mr-2 lg:mr-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-8 px-3 rounded-md border border-white/[0.08] bg-schist/40 hover:bg-schist/70 text-limestone text-[13px] lg:text-sm transition-colors max-w-[280px] lg:max-w-[420px]"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="shrink-0 w-3.5 h-3.5 text-clay" />
      </button>

      {open && (
        <>
          {/* click-away */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-80 max-h-[70vh] overflow-auto z-40 rounded-md border border-white/[0.1] bg-basalt/95 backdrop-blur-md py-1 text-sm">
            {!loaded && (
              <p className="px-3 py-3 text-clay text-xs">A carregar...</p>
            )}

            {loaded && loadError && (
              <p className="px-3 py-3 text-xs" style={{ color: '#D7191C' }}>
                Não foi possível carregar os projetos.
              </p>
            )}

            {loaded && !loadError && projects!.length === 0 && (
              <p className="px-3 py-2 text-clay text-xs">Ainda não tem projetos.</p>
            )}

            {loaded && !loadError && projects!.map((p) => {
              const projOrders = ordersByProject.get(p.id) ?? [];
              const isExpanded = expanded === p.id;
              return (
                <div key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      const next = isExpanded ? null : p.id;
                      setExpanded(next);
                      if (next) focusProject(p);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-schist/60 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 text-clay shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-clay shrink-0" />
                    )}
                    <span className="flex-1 truncate text-limestone">{p.name}</span>
                    <span className="text-clay/70 text-xs">{projOrders.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="pb-1">
                      {projOrders.length === 0 && (
                        <p className="pl-9 pr-3 py-1.5 text-clay/70 text-xs">Sem pedidos.</p>
                      )}
                      {projOrders.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          onClick={() => window.location.assign(orderUrl(o.id))}
                          className="w-full flex items-center justify-between pl-9 pr-3 py-1.5 hover:bg-schist/60 text-left"
                        >
                          <span className="text-limestone text-[13px]">
                            Pedido {shortId(o.id)}
                          </span>
                          <span className="text-clay/70 text-[11px] font-mono">
                            {o.items?.length ?? 0} · {o.status}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Orders not tied to a project (e.g. legacy/demo orders) */}
            {loaded && !loadError && orphanOrders.length > 0 && (
              <div className="border-t border-white/[0.06] mt-1 pt-1">
                <p className="px-3 py-1 text-clay/60 text-[11px] uppercase tracking-wide">Outros pedidos</p>
                {orphanOrders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => window.location.assign(orderUrl(o.id))}
                    className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-schist/60 text-left"
                  >
                    <span className="text-limestone text-[13px]">Pedido {shortId(o.id)}</span>
                    <span className="text-clay/70 text-[11px] font-mono">{o.items?.length ?? 0} · {o.status}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-white/[0.06] mt-1 pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  // Clear any focused-project polygon so the create map is clean,
                  // then open the in-map order panel. Navigate to the bare map if
                  // an order is currently loaded in the URL.
                  setOrderPolygon(null);
                  setOrderBounds(null);
                  if (orderId) navigate('/');
                  setCreateMode(true);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-schist/60 text-left text-terracotta"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span>Criar novo projeto</span>
              </button>
              <button
                type="button"
                onClick={async () => { setOpen(false); await logout(); navigate('/login', { replace: true }); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-schist/60 text-left text-clay"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
